import { ArrowRight, Check } from 'lucide-react';
import { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';

import { cn } from '@/lib/shadcn/utils';
import { FreePlanGate } from '@/modules/billing/components/FreePlanGate';
import { BookingLinkForm } from '@/modules/hiring-manager/components/BookingLinkForm';
import { HMLoadError } from '@/modules/hiring-manager/components/HMLoadError';
import { JobPageHeader } from '@/modules/hiring-manager/components/JobPageHeader';
import { toSearchSuffix } from '@/modules/hiring-manager/utils/searchSuffix';
import { useUpdateJobAutomationMutation } from '@/modules/jobs/mutations/useUpdateJobAutomationMutation';
import { useJobQuery } from '@/modules/jobs/queries/useJobQuery';
import type { JobApi } from '@/modules/jobs/types';
import { Button } from '@/modules/ui/components/button/Button';
import { InlineError } from '@/modules/ui/components/inline-error/InlineError';
import { Skeleton } from '@/modules/ui/components/skeleton/Skeleton';
import { useIsLinkedInConnected } from '@/modules/user-external-accounts/hooks/useIsLinkedInConnected';
import { useCurrentUserQuery } from '@/modules/users/queries/useCurrentUserQuery';
import { LinkedInNotice } from '@/pages/hm/job-candidates/LinkedInNotice';
import { actionErrorMessage } from '@/utils/actionErrorMessage';

import { AutomationUpgradePitch } from './AutomationUpgradePitch';
import { ConfirmSetupDialog, type SetupReview } from './ConfirmSetupDialog';
import {
  automationFromJob,
  automationPatchFor,
  customChoice,
  describeAutomation,
  matchingSetup,
  offersLinkedInConnect,
  type Setup,
  setups,
} from './jobAutomation';
import { SetupPicker } from './SetupPicker';

/** When a staged setup takes effect, by job state. */
const changeHint: Record<JobApi['state'], string> = {
  Published:
    'Nothing changes until you confirm. Candidates already in progress keep their current setup.',
  Unpublished: 'Nothing runs until you publish this job.',
  OnHold: 'The search is paused. The new setup applies when you resume it.',
  Hired: 'This search is finished, so nothing runs on this job.',
  Closed: 'This search is finished, so nothing runs on this job.',
};

/**
 * How much Finlay does on this job, as one of three setups. Picking a setup
 * only stages it; on a live job the change is reviewed in a dialog that
 * spells out what starts or stops, while a draft saves it straight away.
 * The job stays the single source of truth for the saved setup.
 */
export function HMJobAutomation() {
  const { jobId } = useParams<{ jobId: string }>();
  const [searchParams] = useSearchParams();
  const searchSuffix = toSearchSuffix(searchParams);
  const jobQuery = useJobQuery(jobId!);
  const updateAutomation = useUpdateJobAutomationMutation();
  const currentUserQuery = useCurrentUserQuery();
  const { linkedInStatus, retryLinkedInLookup } = useIsLinkedInConnected();
  // The setup picked but not saved yet; null shows the saved one.
  const [chosenKey, setChosenKey] = useState<Setup['key'] | null>(null);
  const [review, setReview] = useState<SetupReview | null>(null);
  const [isReviewOpen, setIsReviewOpen] = useState(false);

  if (jobQuery.isLoading) {
    return (
      <div className="flex flex-col gap-8">
        <div className="flex flex-col gap-5">
          <Skeleton className="h-7 w-72" />
          <Skeleton className="h-4 w-56" />
          <Skeleton className="h-px w-full" />
        </div>
        <div className="flex flex-col gap-2">
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
        </div>
      </div>
    );
  }

  if (!jobQuery.data) {
    const jobNotFound =
      jobQuery.error !== null &&
      'status' in jobQuery.error &&
      jobQuery.error.status === 404;
    if (jobNotFound) {
      return (
        <div className="flex flex-col items-start gap-4">
          <p className="text-muted-foreground">Job not found.</p>
          <Button asChild size="sm" variant="outline">
            <Link to={`/hm${searchSuffix}`}>Back to jobs</Link>
          </Button>
        </div>
      );
    }
    return (
      <HMLoadError
        description="We could not load this job. Check your connection and try again."
        isRetrying={jobQuery.isFetching}
        onRetry={() => void jobQuery.refetch()}
        title="Job unavailable"
      />
    );
  }

  const job = jobQuery.data;
  const values = automationFromJob(job);
  const current = matchingSetup(values);
  const chosen = setups.find(
    (setup) => setup.key === chosenKey && setup.key !== current?.key,
  );
  const selected = chosen ?? current;
  // Nothing runs on an unpublished job, so only a live search reviews the change first.
  const isLive = job.state === 'Published';
  const currentUser = currentUserQuery.data;
  // The owner is the outreach sender, so their booking link is the one candidates get.
  const isOwner = currentUser !== undefined && currentUser.id === job.ownerId;
  const ownerName = job.owner?.firstName ?? 'The job owner';
  // Without a saved link the API would reject screening yourself, so ask for the link first.
  const needsBookingLink =
    chosen?.values.prescreen === 'human' && isOwner && !currentUser?.meetingUrl;
  // Skippable: the API holds outreach until LinkedIn is connected, so saving stays open.
  const offersLinkedIn =
    selected !== undefined &&
    offersLinkedInConnect({
      outreachOn: selected.values.outreach === 'finlay',
      isOwner,
      linkedInStatus,
    });
  const saveError = updateAutomation.error
    ? actionErrorMessage('save this change', updateAutomation.error)
    : null;

  const choose = (setup: Setup) => {
    updateAutomation.reset();
    setChosenKey(setup.key);
  };

  const save = (setup: Setup) =>
    updateAutomation.mutate(
      { jobId: job.id, body: automationPatchFor(setup.values) },
      {
        onSuccess: () => {
          setChosenKey(null);
          setIsReviewOpen(false);
        },
      },
    );

  const reviewChange = (setup: Setup) => {
    if (!isLive) {
      save(setup);
      return;
    }
    updateAutomation.reset();
    setReview({ from: values, to: setup });
    setIsReviewOpen(true);
  };

  const bookingLinkDetails = needsBookingLink ? (
    <div className="flex flex-col gap-2 rounded-[10px] border px-5 py-4">
      <p className="text-muted-foreground text-[13px] leading-[18px]">
        Add the link candidates use to book a call with you. You can change it
        later in Integrations.
      </p>
      <BookingLinkForm
        onCancel={() => setChosenKey(null)}
        submitLabel="Save my link"
      />
    </div>
  ) : selected?.values.prescreen === 'human' ? (
    <p className="text-muted-foreground flex flex-wrap items-center gap-x-1.5 text-[13px] leading-[18px]">
      <Check aria-hidden className="text-semantic-560 size-3.5" />
      {isOwner && currentUser?.meetingUrl ? (
        <>
          <span>Candidates get your booking link:</span>
          <a
            className="text-foreground break-all underline underline-offset-2"
            href={currentUser.meetingUrl}
            rel="noreferrer"
            target="_blank"
          >
            {currentUser.meetingUrl}
          </a>
          <Link
            className="text-primary-700 font-medium"
            to={`/hm/integrations${searchSuffix}`}
          >
            Change
          </Link>
        </>
      ) : (
        <span>
          Candidates get {isOwner ? 'your' : `${ownerName}’s`} booking link.
        </span>
      )}
    </p>
  ) : null;

  return (
    <div className="flex flex-col gap-8">
      <JobPageHeader
        activeTab="automation"
        job={job}
        searchSuffix={searchSuffix}
      />

      <FreePlanGate
        requires="automation"
        upgrade={<AutomationUpgradePitch to={`/hm/billing${searchSuffix}`} />}
      >
        <div className="flex max-w-[880px] flex-col gap-5">
          <div className="flex flex-col gap-1">
            <h2 className="text-base font-semibold">
              How should Finlay run this job?
            </h2>
            <p className="text-muted-foreground text-[13px] leading-[1.5]">
              Finlay always finds and ranks candidates for you, and you always
              make the final call. Pick who handles the steps in between.
            </p>
            {!current && (
              <p className="text-muted-foreground text-[13px] leading-[1.5]">
                This job runs a custom setup. {describeAutomation(values)}{' '}
                Choosing a setup below replaces it.
              </p>
            )}
          </div>

          <SetupPicker
            current={current?.key}
            disabled={updateAutomation.isPending}
            onChange={choose}
            value={selected?.key}
          />

          {offersLinkedIn && (
            <LinkedInNotice
              className="text-muted-foreground text-[13px] leading-[18px]"
              integrationsHref={`/hm/integrations${searchSuffix}`}
              isAutomated
              onRetry={retryLinkedInLookup}
              status={linkedInStatus}
            />
          )}

          {bookingLinkDetails}

          {chosen && (
            <ChangeBar
              error={isReviewOpen ? null : saveError}
              from={current?.title ?? `${customChoice.title} setup`}
              hint={changeHint[job.state]}
              isDisabled={needsBookingLink}
              isSaving={updateAutomation.isPending && !isReviewOpen}
              onCancel={() => {
                updateAutomation.reset();
                setChosenKey(null);
              }}
              onSubmit={() => reviewChange(chosen)}
              submitLabel={isLive ? 'Review change' : 'Save change'}
              to={chosen.title}
            />
          )}
        </div>

        <ConfirmSetupDialog
          bookingLinkOwner={isOwner ? 'your' : `${ownerName}’s`}
          error={isReviewOpen ? saveError : null}
          isLinkedInMissing={offersLinkedIn}
          isSaving={updateAutomation.isPending}
          jobTitle={job.title}
          onConfirm={() => review && save(review.to)}
          onOpenChange={(open) => {
            // Reopening starts clean instead of showing the last attempt's error.
            if (!open) updateAutomation.reset();
            setIsReviewOpen(open);
          }}
          open={isReviewOpen}
          review={review}
        />
      </FreePlanGate>
    </div>
  );
}

interface ChangeBarProps {
  from: string;
  to: string;
  hint: string;
  submitLabel: string;
  isDisabled: boolean;
  isSaving: boolean;
  /** A failed direct save on a draft; a live job shows its failure in the dialog. */
  error: string | null;
  onCancel: () => void;
  onSubmit: () => void;
}

/** The staged switch, with the one action that reviews or saves it. */
function ChangeBar({
  from,
  to,
  hint,
  submitLabel,
  isDisabled,
  isSaving,
  error,
  onCancel,
  onSubmit,
}: ChangeBarProps) {
  return (
    <div
      className={cn(
        'bg-sidebar animate-in fade-in flex flex-col gap-3 rounded-[10px] border py-3.5 pr-4 pl-5 duration-150 motion-reduce:animate-none',
        'sm:flex-row sm:items-center sm:gap-4',
      )}
      role="group"
      aria-label="Unsaved change"
    >
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <p className="flex flex-wrap items-center gap-1.5 text-[13px]">
          <span className="text-muted-foreground">{from}</span>
          <ArrowRight aria-hidden className="size-3.5 text-neutral-500" />
          <span className="sr-only">to</span>
          <span className="font-semibold">{to}</span>
        </p>
        <p className="text-xs text-neutral-500">{hint}</p>
        {error && <InlineError className="mt-1">{error}</InlineError>}
      </div>
      <div className="flex shrink-0 gap-2 self-end sm:self-auto">
        <Button
          className="h-8 rounded-md"
          disabled={isSaving}
          onClick={onCancel}
          size="sm"
          variant="ghost"
        >
          Cancel
        </Button>
        <Button
          className="h-8 rounded-md"
          disabled={isDisabled}
          loading={isSaving}
          onClick={onSubmit}
          size="sm"
        >
          {submitLabel}
        </Button>
      </div>
    </div>
  );
}
